import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTokenFromLink } from '../hooks/useTokenFromLink';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * /admin/accept-invite — public page reached from the staff invite email.
 * Captures the token from the link, strips it from the address bar, verifies it
 * via POST /api/admin/staff/invite-check (token in the body), shows the
 * activation form if valid, posts to POST /staff/accept-invite to set the
 * password, then redirects to /admin/login.
 */
export default function AdminAcceptInvite() {
  const navigate = useNavigate();
  // Capture the token ONCE from the emailed link, then strip it from the address
  // bar (below) so it never lingers in browser history or leaks via Referer to
  // any third-party resource the page loads. A refresh afterward has no token →
  // a "reopen the link" message, not a crash.
  // 🔑 hooks/useTokenFromLink: read once, stripped from the address bar, no-referrer while the page is open.
  const token = useTokenFromLink();
  const [check, setCheck]   = useState({ loading: true, valid: false, reason: null, data: null });
  const [pw, setPw]         = useState('');
  const [pwc, setPwc]       = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showPwc, setShowPwc] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr]   = useState('');

  const verify = useCallback(async () => {
    if (!token) {
      setCheck({ loading: false, valid: false, reason: 'no_token', data: null });
      return;
    }
    try {
      const r = await axios.post(`${API}/api/admin/staff/invite-check`, { token });
      if (r.data.valid) {
        setCheck({ loading: false, valid: true, reason: null, data: r.data });
      } else {
        setCheck({ loading: false, valid: false, reason: r.data.reason || 'not_found', data: null });
      }
    } catch (e) {
      setCheck({ loading: false, valid: false, reason: 'error', data: null });
    }
  }, [token]);

  useEffect(() => { verify(); }, [verify]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pw.length < 8 || !/\d/.test(pw) || !/[A-Z]/.test(pw)) {
      setErr('Password must be at least 8 characters and include a number and an uppercase letter.');
      return;
    }
    if (pw !== pwc) { setErr('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/admin/staff/accept-invite`, { token, password: pw, confirmPassword: pwc });
      setDone(true);
      setTimeout(() => navigate('/admin/login', { replace: true }), 2000);
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not activate your account.');
    } finally { setSubmitting(false); }
  };

  // ── Layout shell ────────────────────────────────────────────────────
  const wrap = { minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
  const card = { maxWidth: 440, width: '100%', background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(58,106,217,0.08)', border: '1px solid #e5e7eb', padding: 36 };
  const h1   = { margin: 0, fontSize: 22, fontWeight: 800, color: '#3a6ad9' };
  const sub  = { margin: '8px 0 0', fontSize: 14, color: '#6b7280' };

  if (check.loading) {
    return (
      <div style={wrap}>
        <div style={card}>
          <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>Verifying invite…</p>
        </div>
      </div>
    );
  }

  if (!check.valid) {
    const reasonCopy = {
      no_token:         'Please reopen the invite link from your email — the link can only be used once per page load.',
      expired:          'This invite link has expired. Contact your administrator for a new one.',
      already_accepted: 'This invite has already been accepted. Sign in instead.',
      not_found:        'This invite link is invalid. Contact your administrator.',
      error:            'We could not verify this invite. Try again or contact your administrator.',
    }[check.reason] || 'This invite link is invalid or has expired. Contact your administrator.';
    return (
      <div style={wrap}>
        <div style={card}>
          <h1 style={h1}>Invite unavailable</h1>
          <p style={sub}>{reasonCopy}</p>
          <button onClick={() => navigate('/admin/login')} type="button"
            style={{ marginTop: 22, width: '100%', padding: '12px 16px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={wrap}>
        <div style={card}>
          <h1 style={{ ...h1, color: '#15803d' }}>✓ Account activated</h1>
          <p style={sub}>Redirecting to sign in…</p>
        </div>
      </div>
    );
  }

  const { name, email, role_label } = check.data || {};
  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <img src="/doorly_logo_black.png" alt="Doorly" style={{ height: 32, marginBottom: 12 }} />
          <h1 style={h1}>Welcome to DoorlyDesk{name ? `, ${name.split(' ')[0]}!` : '!'}</h1>
          <p style={sub}>Set your password to activate your account</p>
        </div>

        {role_label && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
            <span style={{ background: '#acf44e', color: '#1a1a1a', fontSize: 12, fontWeight: 800, padding: '4px 12px', borderRadius: 9999 }}>{role_label}</span>
          </div>
        )}

        <form onSubmit={submit}>
          <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>Email</label>
          <input type="email" value={email || ''} readOnly
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', background: '#f9fafb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 14, color: '#6b7280' }} />

          <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>Create Password</label>
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <input type={showPw ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)} required
              style={{ width: '100%', padding: '10px 38px 10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
            <button type="button" onClick={() => setShowPw((v) => !v)}
              style={{ position: 'absolute', right: 10, top: 0, height: '100%', background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 11 }}>
              {showPw ? 'Hide' : 'Show'}
            </button>
          </div>

          <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, display: 'block', marginBottom: 4 }}>Confirm Password</label>
          <div style={{ position: 'relative', marginBottom: 6 }}>
            <input type={showPwc ? 'text' : 'password'} value={pwc} onChange={(e) => setPwc(e.target.value)} required
              style={{ width: '100%', padding: '10px 38px 10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
            <button type="button" onClick={() => setShowPwc((v) => !v)}
              style={{ position: 'absolute', right: 10, top: 0, height: '100%', background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 11 }}>
              {showPwc ? 'Hide' : 'Show'}
            </button>
          </div>
          <p style={{ margin: '4px 0 16px', fontSize: 11, color: '#9ca3af' }}>At least 8 characters, 1 uppercase letter, 1 number</p>

          {err && (
            <div style={{ marginBottom: 14, padding: '10px 12px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: 8, fontSize: 13, color: '#ff082f' }}>
              {err}
            </div>
          )}

          <button type="submit" disabled={submitting}
            style={{ width: '100%', padding: '12px 16px', background: submitting ? '#93c5fd' : '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: submitting ? 'wait' : 'pointer' }}>
            {submitting ? 'Activating…' : 'Activate Account'}
          </button>
        </form>
      </div>
    </div>
  );
}
